import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function getAdminClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

type Tier = "free" | "tutor" | "center" | "school" | "enterprise";
const PAID_TIERS: Tier[] = ["center", "school", "enterprise"];

async function getUserTier(admin: ReturnType<typeof getAdminClient>, userId: string): Promise<Tier> {
  const { data } = await admin
    .from("subscriptions")
    .select("status, current_period_end, plan")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data || !data.plan) return "free";
  const now = Date.now();
  const periodEnd = data.current_period_end ? new Date(data.current_period_end).getTime() : Infinity;
  const active =
    (["active", "trialing", "past_due"].includes(data.status) && periodEnd > now) ||
    (data.status === "canceled" && periodEnd > now);
  if (!active) return "free";
  return (PAID_TIERS.includes(data.plan as Tier) || data.plan === "tutor") ? (data.plan as Tier) : "free";
}

async function getCenterTier(admin: ReturnType<typeof getAdminClient>, centerId: string): Promise<Tier> {
  // Find admin user(s) of this center, pick highest tier
  const { data: profs } = await admin.from("profiles").select("id").eq("center_id", centerId);
  const ids = (profs ?? []).map((p) => p.id);
  if (!ids.length) return "free";
  const { data: roles } = await admin.from("user_roles").select("user_id, role").in("user_id", ids).eq("role", "admin");
  const adminIds = (roles ?? []).map((r) => r.user_id);
  if (!adminIds.length) return "free";
  let best: Tier = "free";
  const rank: Record<Tier, number> = { free: 0, tutor: 1, center: 2, school: 3, enterprise: 4 };
  for (const aid of adminIds) {
    const t = await getUserTier(admin, aid);
    if (rank[t] > rank[best]) best = t;
  }
  return best;
}

async function getCallerCenterAndRole(admin: ReturnType<typeof getAdminClient>, userId: string) {
  const { data: profile } = await admin.from("profiles").select("center_id").eq("id", userId).maybeSingle();
  const centerId = profile?.center_id ?? null;
  const { data: roleRows } = await admin.from("user_roles").select("role").eq("user_id", userId);
  const roles = (roleRows ?? []).map((r) => r.role as "admin" | "teacher" | "student");
  return { centerId, roles };
}

async function assertDocumentsEnabled(admin: ReturnType<typeof getAdminClient>, centerId: string) {
  const tier = await getCenterTier(admin, centerId);
  if (!PAID_TIERS.includes(tier)) {
    throw new Error("Tính năng Tài liệu chỉ có ở gói Trung tâm trở lên.");
  }
}

export const listDocuments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = getAdminClient();
    const { centerId, roles } = await getCallerCenterAndRole(admin, context.userId);
    if (!centerId) return { folders: [], documents: [] };
    await assertDocumentsEnabled(admin, centerId);

    const { data: folders } = await admin
      .from("document_folders")
      .select("id, name, color, created_at")
      .eq("center_id", centerId)
      .order("name");

    let query = admin
      .from("documents")
      .select("id, folder_id, title, description, doc_type, file_path, external_url, file_size, mime_type, visible_admin, visible_teacher, visible_student, created_at, uploaded_by")
      .eq("center_id", centerId)
      .order("created_at", { ascending: false });

    if (roles.includes("admin")) {
      // admin sees all
    } else if (roles.includes("teacher")) {
      query = query.eq("visible_teacher", true);
    } else if (roles.includes("student")) {
      query = query.eq("visible_student", true);
    } else {
      return { folders: folders ?? [], documents: [] };
    }

    const { data: docs, error } = await query;
    if (error) throw new Error(error.message);

    const out = await Promise.all(
      (docs ?? []).map(async (d) => {
        let signedUrl: string | null = null;
        if (d.doc_type === "file" && d.file_path) {
          const { data } = await admin.storage.from("documents").createSignedUrl(d.file_path, 3600);
          signedUrl = data?.signedUrl ?? null;
        }
        return { ...d, signed_url: signedUrl };
      }),
    );

    return { folders: folders ?? [], documents: out };
  });

async function assertAdminOfCenter(admin: ReturnType<typeof getAdminClient>, userId: string) {
  const { centerId, roles } = await getCallerCenterAndRole(admin, userId);
  if (!centerId || !roles.includes("admin")) {
    throw new Error("Chỉ quản trị viên trung tâm mới được thực hiện thao tác này.");
  }
  await assertDocumentsEnabled(admin, centerId);
  return centerId;
}

export const createFolder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { name: string; color?: string }) =>
    z.object({ name: z.string().min(1).max(120), color: z.string().max(20).optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    const { data: row, error } = await admin
      .from("document_folders")
      .insert({ name: data.name.trim(), color: data.color ?? "blue", center_id: centerId, created_by: context.userId })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const deleteFolder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    const { error } = await admin.from("document_folders").delete().eq("id", data.id).eq("center_id", centerId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const DocSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  folder_id: z.string().uuid().optional().nullable(),
  doc_type: z.enum(["file", "link"]),
  file_path: z.string().max(500).optional().nullable(),
  external_url: z.string().url().max(2000).optional().nullable(),
  file_size: z.number().int().nonnegative().optional().nullable(),
  mime_type: z.string().max(120).optional().nullable(),
  visible_admin: z.boolean().default(true),
  visible_teacher: z.boolean().default(true),
  visible_student: z.boolean().default(false),
});

export const createDocument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DocSchema.parse(d))
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    if (data.doc_type === "file" && !data.file_path) throw new Error("Thiếu file_path");
    if (data.doc_type === "link" && !data.external_url) throw new Error("Thiếu external_url");
    const { data: row, error } = await admin
      .from("documents")
      .insert({
        center_id: centerId,
        folder_id: data.folder_id ?? null,
        title: data.title.trim(),
        description: data.description ?? null,
        doc_type: data.doc_type,
        file_path: data.doc_type === "file" ? data.file_path : null,
        external_url: data.doc_type === "link" ? data.external_url : null,
        file_size: data.file_size ?? null,
        mime_type: data.mime_type ?? null,
        visible_admin: data.visible_admin,
        visible_teacher: data.visible_teacher,
        visible_student: data.visible_student,
        uploaded_by: context.userId,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const updateDocument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid(),
      title: z.string().min(1).max(200).optional(),
      description: z.string().max(2000).nullable().optional(),
      folder_id: z.string().uuid().nullable().optional(),
      visible_admin: z.boolean().optional(),
      visible_teacher: z.boolean().optional(),
      visible_student: z.boolean().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    const { id, ...patch } = data;
    const { error } = await admin.from("documents").update(patch).eq("id", id).eq("center_id", centerId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteDocument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    const { data: row } = await admin
      .from("documents")
      .select("file_path, doc_type")
      .eq("id", data.id)
      .eq("center_id", centerId)
      .maybeSingle();
    if (row?.doc_type === "file" && row.file_path) {
      await admin.storage.from("documents").remove([row.file_path]);
    }
    const { error } = await admin.from("documents").delete().eq("id", data.id).eq("center_id", centerId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createDocumentUploadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { filename: string }) =>
    z.object({ filename: z.string().min(1).max(255) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const admin = getAdminClient();
    const centerId = await assertAdminOfCenter(admin, context.userId);
    const safe = data.filename.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${centerId}/${crypto.randomUUID()}-${safe}`;
    const { data: signed, error } = await admin.storage.from("documents").createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });
